import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button";
import { ChevronRight, ArrowLeft } from "lucide-react";
import axiosInstance from '../utils/axiosConfig';

const ResetPassword = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [token, setToken] = useState('');
    const { resettoken } = useParams();
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });

    useEffect(() => { 
        if (resettoken) {
            setToken(resettoken);
        } else {
            setError(t('resetPassword.invalidToken'));
        }
    }, [resettoken, t]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError(t('resetPassword.passwordsDontMatch'));
            setIsLoading(false);
            return;
        }

        if (formData.password.length < 8) {
            setError(t('resetPassword.passwordTooShort'));
            setIsLoading(false);
            return;
        }

        try {
            await axiosInstance.post('/auth/ResetPassword', {
                token: token,
                newPassword: formData.password
            });

            setSuccess(t('resetPassword.success'));

            // Redirect na 3 seconden
            setTimeout(() => {
                navigate('/');
            }, 3000);

        } catch (err) {
            console.error('Reset password error:', err);

            if (err.response) {
                setError(err.response.data || t('resetPassword.error'));
            } else if (err.request) {
                setError(t('auth.connectionError'));
            } else {
                setError(t('resetPassword.error'));
            }
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full space-y-6 p-8 bg-white rounded-lg shadow-lg">
                {/* Header Section */}
                <div className="text-center">
                    <h2 className="text-3xl font-inter text-ps-blue-dark">
                        {t('resetPassword.title')}
                    </h2>
                    <p className="mt-2 text-sm text-gray-600 font-inter">
                        {t('resetPassword.subtitle')}
                    </p>
                </div>

                {/* Form Section */}
                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    {/* Error Display */}
                    {error && (
                        <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded font-inter">
                            {error}
                        </div>
                    )}

                    {/* Success Display */}
                    {success && (
                        <div className="text-green-600 text-sm text-center bg-green-50 p-3 rounded font-inter">
                            {success}
                        </div>
                    )}

                    {/* Password Fields */}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                {t('resetPassword.newPassword')}
                            </label>
                            <Input
                                type="password"
                                name="password"
                                required
                                value={formData.password}
                                onChange={handleChange}
                                className="w-full p-2 border rounded bg-white font-inter"
                                placeholder="••••••••"
                                minLength={8}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                {t('resetPassword.confirmPassword')}
                            </label>
                            <Input
                                type="password"
                                name="confirmPassword"
                                required
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className="w-full p-2 border rounded bg-white font-inter"
                                placeholder="••••••••"
                                minLength={8}
                            />
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div>
                        <Button
                            type="submit"
                            disabled={isLoading || !token}
                            className="w-full bg-ps-blue-dark hover:opacity-80 text-white flex items-center justify-center gap-2 h-12 font-inter"
                        >
                            {isLoading ? t('resetPassword.saving') : t('resetPassword.changePassword')}
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Back to Login */}
                    <div className="text-center">
                        <Button
                            type="button"
                            variant="link"
                            onClick={() => navigate('/')}
                            className="text-ps-blue-dark hover:text-ps-blue font-medium flex items-center justify-center gap-2"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            {t('resetPassword.backToLogin')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;